import { isDynamicRouteSegment, isUuid, STATUS_CODE } from '@kc-console/core';

import {
  SERVER_TRACE_HEADERS,
  SERVER_TRACE_MAX_SPANS,
  SERVER_TRACE_SCHEMA_VERSION,
} from './constants';
import { logServerTrace } from './logger';
import {
  getServerTraceSlowThresholdMs,
  isServerTraceEnabled,
  shouldSampleServerTrace,
} from './sampling';

import type { GetServerSidePropsContext } from 'next';
import type { HttpClientTimingObserver } from '@kc-console/core';
import type {
  ServerTraceCategory,
  ServerTraceExecution,
  ServerTraceExecutionHandle,
  ServerTraceExecutionResult,
  ServerTraceOutcome,
  ServerTraceRequestMode,
  ServerTraceSpan,
  ServerTraceSpanHandle,
  ServerTraceSpanMeta,
  ServerTraceSummary,
  StartServerTraceSpanParams,
} from './types';

/**
 * 하나의 Node HTTP 요청에서 수집되는 mutable trace 상태입니다.
 * 응답이 완료될 때까지 여러 Guard executor가 같은 레코드를 공유할 수 있습니다.
 */
type ServerTraceRecord = {
  activeExecutions: number;
  executorMs: number;
  finalized: boolean;
  groupSequence: number;
  guardsMs: number;
  host: string;
  listenerRegistered: boolean;
  normalizedRoute: string;
  outcome: ServerTraceOutcome;
  request: GetServerSidePropsContext['req'];
  requestId: string;
  requestMode: ServerTraceRequestMode;
  response: GetServerSidePropsContext['res'];
  responseCompleted: boolean;
  sampled: boolean;
  sequence: number;
  service: string;
  spans: ServerTraceSpan[];
  startedAt: number;
  truncated: boolean;
  lastExecutorSettledAt?: number;
  middlewareMs?: number;
  onClose?: () => void;
  onFinish?: () => void;
  responseFinishedAt?: number;
  statusCode?: number;
  terminalGuard?: string;
};

// 요청이 종료되면 GC 대상이 될 수 있도록 요청 객체를 약한 참조 키로 사용합니다.
const traceStore = new WeakMap<GetServerSidePropsContext['req'], ServerTraceRecord>();
const noopServerTraceExecutionHandle: ServerTraceExecutionHandle = {
  end: () => undefined,
};
const noopServerTraceSpanHandle: ServerTraceSpanHandle = {
  end: () => undefined,
};

// 여러 결과가 발생한 경우 요청을 가장 심각한 결과 하나로 요약하기 위한 우선순위입니다.
const OUTCOME_PRIORITY: Record<ServerTraceOutcome, number> = {
  success: 0,
  expected_rejection: 0,
  fail_open: 0,
  redirect: 1,
  failed_status: 2,
  rejected: 3,
  aborted: 4,
};

const SERVICE_PATTERN = /^[a-z0-9-]{1,64}$/;

const now = () => globalThis.performance?.now() ?? Date.now();
const roundMilliseconds = (value: number) => Math.round(value * 100) / 100;

const getHeaderValue = (value: string | string[] | undefined) => {
  if (Array.isArray(value)) return value[0];
  return value;
};

const createRequestId = () => globalThis.crypto.randomUUID();

const getRequestId = (context: GetServerSidePropsContext) => {
  const requestId = getHeaderValue(context.req.headers[SERVER_TRACE_HEADERS.REQUEST_ID]);
  return requestId && isUuid(requestId) ? requestId : createRequestId();
};

const getMiddlewareDuration = (context: GetServerSidePropsContext) => {
  const value = getHeaderValue(context.req.headers[SERVER_TRACE_HEADERS.MIDDLEWARE_DURATION]);
  const duration = Number(value);

  if (!Number.isFinite(duration) || duration < 0 || duration > 60_000) return undefined;
  return roundMilliseconds(duration);
};

const getDynamicParamSegments = (context: GetServerSidePropsContext) => {
  const dynamicSegments = new Map<string, string>();

  Object.entries(context.params ?? {}).forEach(([key, value]) => {
    const values = Array.isArray(value) ? value.slice(1) : [value];
    const paramName = /^[a-zA-Z0-9_-]+$/.test(key) ? key : 'param';

    values
      .filter((segment): segment is string => Boolean(segment))
      .forEach(segment => {
        dynamicSegments.set(segment, `:${paramName}`);
        dynamicSegments.set(encodeURIComponent(segment), `:${paramName}`);
      });
  });

  return dynamicSegments;
};

/**
 * 쿼리와 리소스 식별자를 제거하여 Kibana 필드 cardinality와 민감 정보 노출을 줄입니다.
 * Next.js params로 확인되는 값은 `:paramName`, 식별자 형태의 값은 `:id`로 치환합니다.
 */
const normalizeRoute = (context: GetServerSidePropsContext) => {
  const rawPath = (context.resolvedUrl || context.req.url || '/').split('?')[0] || '/';
  const dynamicParamSegments = getDynamicParamSegments(context);
  const segments = rawPath.split('/').map(segment => {
    if (!segment) return segment;
    const paramName = dynamicParamSegments.get(segment);
    if (paramName) return paramName;
    return isDynamicRouteSegment(segment) ? ':id' : segment;
  });

  return segments.join('/') || '/';
};

const getService = (normalizedRoute: string) => {
  const segments = normalizedRoute.split('/').filter(Boolean);
  const firstSegment = segments[0];

  if (firstSegment && /^[a-z]{2}(?:-[A-Z]{2})?$/.test(firstSegment)) {
    return SERVICE_PATTERN.test(segments[1] || '') ? segments[1] : 'unknown';
  }

  return SERVICE_PATTERN.test(firstSegment || '') ? firstSegment : 'unknown';
};

const normalizeService = (service: string | undefined, fallback: string) => {
  return service && SERVICE_PATTERN.test(service) ? service : fallback;
};

const updateOutcome = (trace: ServerTraceRecord, outcome: ServerTraceOutcome) => {
  if (OUTCOME_PRIORITY[outcome] > OUTCOME_PRIORITY[trace.outcome]) {
    trace.outcome = outcome;
  }
};

/**
 * 병렬 실행된 인증 span의 겹치는 구간을 합쳐 실제 wall-clock 인증 시간을 계산합니다.
 * 각 duration을 단순 합산하면 병렬 Guard 시간이 중복 계산되므로 interval union을 사용합니다.
 */
const getAuthenticationDuration = (spans: ServerTraceSpan[]) => {
  const intervals = spans
    .filter(span => span.category === 'authentication')
    .map(span => ({
      start: span.startOffsetMs,
      end: span.startOffsetMs + span.durationMs,
    }))
    .sort((a, b) => a.start - b.start);

  if (intervals.length === 0) return 0;

  let total = 0;
  let currentStart = intervals[0].start;
  let currentEnd = intervals[0].end;

  intervals.slice(1).forEach(interval => {
    if (interval.start <= currentEnd) {
      currentEnd = Math.max(currentEnd, interval.end);
      return;
    }

    total += currentEnd - currentStart;
    currentStart = interval.start;
    currentEnd = interval.end;
  });

  return roundMilliseconds(total + currentEnd - currentStart);
};

/**
 * mutable 레코드를 Kibana에 적재할 불변 요청 요약으로 변환합니다.
 *
 * - `nodeResponseFromExecutorMs`: 최초 executor 시작부터 Node 응답 완료까지
 * - `nextAfterExecutorMs`: 마지막 executor 종료 후 Next.js가 응답을 끝내기까지
 */
const createSummary = (trace: ServerTraceRecord): ServerTraceSummary => {
  const responseFinishedAt = trace.responseFinishedAt ?? now();
  const lastExecutorSettledAt = trace.lastExecutorSettledAt ?? responseFinishedAt;

  return {
    schemaVersion: SERVER_TRACE_SCHEMA_VERSION,
    requestId: trace.requestId,
    release: process.env.KC_RELEASE_TAG ?? 'unknown',
    host: trace.host,
    service: trace.service,
    normalizedRoute: trace.normalizedRoute,
    requestMode: trace.requestMode,
    outcome: trace.outcome,
    statusCode: trace.statusCode,
    middlewareMs: trace.middlewareMs,
    guardsMs: roundMilliseconds(trace.guardsMs),
    executorMs: roundMilliseconds(trace.executorMs),
    nodeResponseFromExecutorMs: roundMilliseconds(responseFinishedAt - trace.startedAt),
    nextAfterExecutorMs: roundMilliseconds(Math.max(responseFinishedAt - lastExecutorSettledAt, 0)),
    terminalGuard: trace.terminalGuard,
    truncated: trace.truncated,
    spans: [...trace.spans].sort((a, b) => a.sequence - b.sequence),
  };
};

/**
 * 정상 요청은 샘플링하고, 느린 요청·비정상 종료·서버 오류는 샘플링 여부와 무관하게 남깁니다.
 */
const shouldEmitTrace = (trace: ServerTraceRecord, summary: ServerTraceSummary) => {
  if (!isServerTraceEnabled()) return false;
  if (trace.sampled) return true;
  if (summary.nodeResponseFromExecutorMs >= getServerTraceSlowThresholdMs()) return true;
  if (summary.outcome === 'rejected' || summary.outcome === 'aborted') return true;
  return (summary.statusCode ?? 0) >= STATUS_CODE.INTERNAL_SERVER_ERROR;
};

/**
 * 응답 완료와 모든 executor 종료가 모두 확인된 뒤 로그를 한 번만 출력합니다.
 * 둘 중 하나라도 진행 중이면 마지막 이벤트가 다시 finalize를 호출합니다.
 */
const finalizeTrace = (trace: ServerTraceRecord) => {
  if (trace.finalized || !trace.responseCompleted || trace.activeExecutions > 0) return;

  trace.finalized = true;

  if (trace.onFinish) trace.response.removeListener('finish', trace.onFinish);
  if (trace.onClose) trace.response.removeListener('close', trace.onClose);

  const summary = createSummary(trace);
  if (shouldEmitTrace(trace, summary)) logServerTrace(summary);

  traceStore.delete(trace.request);
};

/**
 * 정상 응답은 `finish`, 연결 중단은 `close` 이벤트에서 종료 시각과 결과를 확정합니다.
 */
const registerResponseListeners = (trace: ServerTraceRecord) => {
  if (trace.listenerRegistered) return;

  trace.listenerRegistered = true;
  trace.onFinish = () => {
    trace.responseCompleted = true;
    trace.responseFinishedAt = now();
    finalizeTrace(trace);
  };
  trace.onClose = () => {
    if (trace.responseCompleted) return;

    trace.responseCompleted = true;
    trace.responseFinishedAt = now();

    if (!trace.response.writableFinished) {
      updateOutcome(trace, 'aborted');
    }

    finalizeTrace(trace);
  };

  trace.response.once('finish', trace.onFinish);
  trace.response.once('close', trace.onClose);
};

const getOrCreateTrace = (
  context: GetServerSidePropsContext,
  requestMode: ServerTraceRequestMode,
) => {
  // 동일 HTTP 요청에서 executor가 여러 번 실행되어도 하나의 요청 로그로 합칩니다.
  const existingTrace = traceStore.get(context.req);
  if (existingTrace) {
    registerResponseListeners(existingTrace);
    return existingTrace;
  }

  const normalizedRoute = normalizeRoute(context);
  const requestId = getRequestId(context);
  const trace: ServerTraceRecord = {
    request: context.req,
    response: context.res,
    requestId,
    host: process.env.KC_SERVER_TRACE_HOST || 'console',
    service: getService(normalizedRoute),
    normalizedRoute,
    requestMode,
    middlewareMs: getMiddlewareDuration(context),
    startedAt: now(),
    sequence: 0,
    groupSequence: 0,
    activeExecutions: 0,
    guardsMs: 0,
    executorMs: 0,
    outcome: 'success',
    spans: [],
    truncated: false,
    sampled: shouldSampleServerTrace(requestId),
    listenerRegistered: false,
    responseCompleted: false,
    finalized: false,
  };

  traceStore.set(context.req, trace);
  registerResponseListeners(trace);

  return trace;
};

/**
 * Guard executor 한 번의 수명 주기를 시작합니다.
 * 반환된 handle의 `end`는 중복 호출에 안전하며 요청 공통 레코드에 결과를 합산합니다.
 */
export const beginServerTraceExecution = (
  context: GetServerSidePropsContext,
  requestMode: ServerTraceRequestMode,
): ServerTraceExecutionHandle => {
  if (!isServerTraceEnabled()) return noopServerTraceExecutionHandle;

  const trace = getOrCreateTrace(context, requestMode);
  const startedAt = now();
  let ended = false;

  trace.activeExecutions += 1;

  return {
    end: (result: ServerTraceExecutionResult) => {
      if (ended) return;
      ended = true;

      const settledAt = now();
      trace.executorMs += settledAt - startedAt;
      trace.lastExecutorSettledAt = settledAt;
      trace.activeExecutions = Math.max(trace.activeExecutions - 1, 0);
      trace.statusCode = result.statusCode ?? trace.statusCode;
      trace.service = normalizeService(result.service, trace.service);
      trace.terminalGuard = result.terminalGuard || trace.terminalGuard;
      updateOutcome(trace, result.outcome);
      finalizeTrace(trace);
    },
  };
};

/**
 * serial/parallel composer 실행 묶음을 Kibana에서 재구성할 수 있는 그룹 ID를 생성합니다.
 */
export const createServerTraceGroupId = (
  request: GetServerSidePropsContext['req'],
  execution: ServerTraceExecution,
) => {
  const trace = traceStore.get(request);
  if (!trace) return `${execution}-unknown`;

  trace.groupSequence += 1;
  return `${execution}-${trace.groupSequence}`;
};

/**
 * Guard 또는 Guard 내부 단계 하나를 요청 trace의 span으로 기록합니다.
 * span 제한을 초과하면 요청 자체는 계속 처리하고 `truncated=true`만 남깁니다.
 */
export const startServerTraceSpan = (
  request: GetServerSidePropsContext['req'],
  params: StartServerTraceSpanParams,
): ServerTraceSpanHandle => {
  const trace = traceStore.get(request);
  if (!trace || !params.name) return noopServerTraceSpanHandle;

  if (trace.spans.length >= SERVER_TRACE_MAX_SPANS) {
    trace.truncated = true;
    return noopServerTraceSpanHandle;
  }

  const startedAt = now();
  const span: ServerTraceSpan = {
    sequence: ++trace.sequence,
    groupId: params.groupId,
    execution: params.execution,
    name: params.name,
    category: params.category,
    startOffsetMs: roundMilliseconds(startedAt - trace.startedAt),
    durationMs: 0,
    outcome: 'success',
  };
  let ended = false;

  trace.spans.push(span);

  return {
    end: (outcome = 'success', statusCode) => {
      // Guard 예외 처리 경로에서 중복 종료되더라도 최초 결과를 보존합니다.
      if (ended) return;
      ended = true;

      span.durationMs = roundMilliseconds(now() - startedAt);
      span.outcome = outcome;
      span.statusCode = statusCode;

      if (outcome === 'failed_status' || outcome === 'redirect' || outcome === 'rejected') {
        trace.terminalGuard = span.name;
      }
    },
  };
};

/**
 * span handle은 executor가 들고 있어 Guard 내부에서는 접근할 수 없으므로 이름으로 찾습니다.
 * 같은 이름이 여러 번 실행됐다면 마지막(현재 실행 중) span에 기록합니다.
 */
export const recordServerTraceSpanMeta = (
  request: GetServerSidePropsContext['req'],
  spanName: string,
  meta: ServerTraceSpanMeta,
) => {
  const trace = traceStore.get(request);
  if (!trace) return;

  for (let index = trace.spans.length - 1; index >= 0; index -= 1) {
    const span = trace.spans[index];
    if (span.name !== spanName) continue;

    span.meta = { ...span.meta, ...meta };
    return;
  }
};

export const addServerTraceGuardsDuration = (
  request: GetServerSidePropsContext['req'],
  durationMs: number,
) => {
  const trace = traceStore.get(request);
  if (trace) trace.guardsMs += durationMs;
};

export const setServerTraceTerminalGuard = (
  request: GetServerSidePropsContext['req'],
  guardName: string,
) => {
  const trace = traceStore.get(request);
  if (trace && guardName) trace.terminalGuard = guardName;
};

/**
 * 브라우저 개발자 도구에서 주요 서버 구간을 확인할 수 있도록 응답 헤더를 추가합니다.
 * 기존 Server-Timing 값은 보존하되 이 모듈이 관리하는 metric만 최신 값으로 교체합니다.
 */
export const appendServerTraceResponseHeaders = (context: GetServerSidePropsContext) => {
  const trace = traceStore.get(context.req);
  if (!trace || context.res.headersSent) return;

  const authDuration = getAuthenticationDuration(trace.spans);
  const metrics = [
    trace.middlewareMs === undefined ? undefined : `mw;dur=${trace.middlewareMs}`,
    `guards;dur=${roundMilliseconds(trace.guardsMs)}`,
    authDuration > 0 ? `auth;dur=${authDuration}` : undefined,
    `executor;dur=${roundMilliseconds(trace.executorMs)}`,
  ].filter((metric): metric is string => Boolean(metric));

  const existingHeader = context.res.getHeader('Server-Timing');
  const existingMetrics = (
    Array.isArray(existingHeader) ? existingHeader.join(',') : String(existingHeader || '')
  )
    .split(',')
    .map(metric => metric.trim())
    .filter(Boolean)
    .filter(metric => !/^(?:mw|guards|auth|executor);/.test(metric));

  context.res.setHeader('Server-Timing', [...existingMetrics, ...metrics].join(', '));
  context.res.setHeader(SERVER_TRACE_HEADERS.RESPONSE_REQUEST_ID, trace.requestId);
};

/**
 * 현재 SSR 요청의 trace id를 반환
 * @description
 * - 로깅이 꺼져 있으면(KC_PERFORMANCE_LOGGING !== 'true') trace 레코드가 없으므로 요청 헤더로 폴백
 * - 둘 다 없으면 undefined를 반환하며, 호출자는 헤더를 주입하지 않음.
 */
export const getServerTraceRequestId = (request: GetServerSidePropsContext['req']) => {
  const tracedRequestId = traceStore.get(request)?.requestId;
  if (tracedRequestId) return tracedRequestId;

  const headerRequestId = getHeaderValue(request.headers[SERVER_TRACE_HEADERS.REQUEST_ID]);
  return headerRequestId && isUuid(headerRequestId) ? headerRequestId : undefined;
};

/**
 * 공통 Axios timing event를 현재 SSR 요청의 자식 span으로 연결합니다.
 * API 서버 수정 없이 Console에서 관찰한 HTTP 왕복 시간만 기록합니다.
 */
export const getServerTraceHttpTimingOptions = (
  request: GetServerSidePropsContext['req'],
  targetService: string,
  parentSpanName?: string,
) => {
  const requestId = getServerTraceRequestId(request);
  const trace = traceStore.get(request);
  if (!trace) return { requestId };

  const parentSpan =
    [...trace.spans].reverse().find(span => parentSpanName && span.name === parentSpanName) ??
    [...trace.spans].reverse().find(span => span.category !== 'http-client');

  const onHttpTiming: HttpClientTimingObserver = event => {
    if (trace.finalized) return;

    if (trace.spans.length >= SERVER_TRACE_MAX_SPANS) {
      trace.truncated = true;
      return;
    }

    const service = normalizeService(targetService, 'unknown');
    const outcome: ServerTraceOutcome =
      event.outcome === 'success' ? 'success' : event.statusCode ? 'failed_status' : 'rejected';
    const meta: ServerTraceSpanMeta = {
      service,
      method: event.method,
      route: event.route,
    };

    if (parentSpan) meta.parentGuard = parentSpan.name;

    trace.spans.push({
      sequence: ++trace.sequence,
      groupId: parentSpan?.groupId ?? 'http-client',
      execution: parentSpan?.execution ?? 'serial',
      name: 'http.' + service,
      category: 'http-client',
      startOffsetMs: roundMilliseconds(Math.max(event.startedAt - trace.startedAt, 0)),
      durationMs: roundMilliseconds(event.durationMs),
      outcome,
      statusCode: event.statusCode,
      meta,
    });
  };

  return { requestId, onHttpTiming };
};

export const getServerTraceSpans = (request: GetServerSidePropsContext['req']) => {
  return traceStore.get(request)?.spans ?? [];
};

// 명시적으로 분류하지 않은 Guard도 추적에서 제외하지 않고 context로 기록합니다.
export const getServerTraceCategory = (
  category: ServerTraceCategory | undefined,
): ServerTraceCategory => category ?? 'context';
